import React, { useState, useEffect, useCallback } from 'react';
import { useRouter } from 'next/router';
import { Form, Input, Button, Upload, Checkbox, message } from 'antd';
import OSS from 'ali-oss';
import styles from './index.module.scss';
import { ServicesIcon, CancelIcon } from '@/components/UI/svg/Ecocredits';
import { loginUser } from '@/utils/cookie';
import { getCountryRegion } from '@/utils/countryRegion';
import { plusMemberCustomerService } from '@/service/vip';
import { useGlobal } from '@/utils/useGlobal';

/**
 * 日本专属客服弹窗
 * @returns
 */
const DedicatedCustomer: React.FC = () => {
    const [open, setOpen] = useState<any>(false);
    const [form] = Form.useForm();
    const { locale } = useRouter();
    const info = loginUser();
    const [agreed, setAgreed] = useState<boolean>(false);
    const [ossClient, setOssClient] = useState<any>(null);
    const [modalBottom, setModalBottom] = useState<number>(0);
    const [sendStatus, setSendStatus] = useState<boolean>(false);
    const isEU = getCountryRegion(locale) === 'eu';
    const HOST_STS = isEU ? process.env.NEXT_PUBLIC_STS_HOST_EU : process.env.NEXT_PUBLIC_STS_HOST;
    const { isMobile } = useGlobal();
    const fileUploadErrorTips = '入力内容に誤りがあります。入力内容を今一度確認して、再度入力してください。';

    const handleOpen = () => {
        setOpen(true);
    };

    const handleCancel = () => {
        setOpen(false);
    };

    const handleChangeAgreed = (e: any) => {
        setAgreed(e.target.checked);
    };

    const handleBeforeUpload = (file: any) => {
        if (form.getFieldValue('fileUrlList')?.length >= 10) {
            message.error(fileUploadErrorTips);
            return Upload.LIST_IGNORE;
        }
        if (file.type.includes('image/') || file.type.includes('application/pdf')) {
            if (file.size > 5 * 1024 * 1024) {
                message.error(fileUploadErrorTips);
                return Upload.LIST_IGNORE;
            }
            return Promise.resolve();
        }
        message.error(fileUploadErrorTips);
        return Upload.LIST_IGNORE;
    };

    const onValuesChange = (changedValues: any, allValues: any) => {
        if (allValues.customerName && allValues.email && allValues.content) {
            setSendStatus(true);
        } else {
            setSendStatus(false);
        }
    };

    const customRequest = ({ file, onSuccess, onError }: any) => {
        const timestamp = new Date().getTime();
        const url = `website/plusmember/${locale}/${timestamp}/${file?.name}`;

        ossClient
            ?.put(url, file, {
                headers: {
                    'x-oss-object-acl': 'private', // 设为私有，不可直接访问
                },
            })
            .then((resData: any) => {
                if (resData?.url) {
                    onSuccess(resData.url);
                    getContentScorll();
                } else {
                    onSuccess('');
                }
            });
    };

    const getContentScorll = () => {
        const scrollContainer = document.getElementById('question-dedicated-content');
        if (scrollContainer) {
            scrollContainer.scrollTop = scrollContainer?.scrollHeight;
        }
    };

    const handleSubmit = () => {
        form.validateFields().then(async (valid: any) => {
            if (valid) {
                const res = await plusMemberCustomerService(locale as string, { ...valid, agreed });
                if (res.code === '0') {
                    setOpen(false);
                    message.success('お問い合わせ受付が完了いたしました。お問い合わせいただきました内容を確認後、担当者よりご連絡いたします。');
                    form.resetFields();
                    setAgreed(false);
                    setSendStatus(false);
                }
            }
        });
    };

    const handleUpload = (newFileList: any) => {
        const newFileUrls: any = [];
        newFileList.fileList?.forEach((item: any) => {
            if (item.status === 'done' && item.response) {
                newFileUrls.push(item.response);
            }
        });

        form.setFieldsValue({
            fileUrlList: newFileUrls,
        });
    };

    const getClient = useCallback(async () => {
        try {
            // 获取init参数
            const result1 = await fetch(`${HOST_STS}/system/oss/fileUpload/prepareSts`, {
                headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
            });
            const json1 = await result1.json();
            const result2 = await fetch(`${HOST_STS}/system/oss/fileUpload/stsServer`, {
                headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
            });
            const json2 = await result2.json();
            if (json1?.code === '0' && json2?.StatusCode === '200') {
                const { AccessKeyId: accessKeyId, AccessKeySecret: accessKeySecret, SecurityToken: stsToken } = json2;
                const { bucket, endpoint, region } = json1.data;

                const client = new OSS({
                    // yourRegion填写Bucket所在地域。以华东1（杭州）为例，yourRegion填写为oss-cn-hangzhou。
                    region,
                    // 从STS服务获取的临时访问密钥（AccessKey ID和AccessKey Secret）。
                    accessKeyId,
                    accessKeySecret,
                    // 从STS服务获取的安全令牌（SecurityToken）。
                    stsToken,
                    // 填写Bucket名称。
                    bucket,
                });
                setOssClient(client);
            } else {
                setOssClient(null);
            }
        } catch (error) {
            setOssClient(null);
            console.log('error--->', error);
        }
    }, [HOST_STS]);

    useEffect(() => {
        if (open) {
            getClient();
        }
    }, [open, getClient]);

    useEffect(() => {
        if (window && !isMobile) {
            const innerH = window.innerHeight;
            if (innerH && innerH < 805) {
                setModalBottom(-100);
            }
        }
    }, []);

    return (
        <div className={styles.DedicatedCustomer}>
            <div className="service-box">
                <div className="icon-outer" onClick={handleOpen}>
                    <ServicesIcon />
                </div>
                <div
                    className={`dedicated-modal`}
                    style={modalBottom ? { display: open ? 'block' : 'none', bottom: `${modalBottom}px` } : { display: open ? 'block' : 'none' }}
                >
                    <div className="dedicated-header">
                        <div className="dedicated-title">お問い合わせ</div>
                        <CancelIcon onClick={handleCancel} />
                    </div>
                    <div className="dedicated-content" id="question-dedicated-content">
                        <div className="dedicated-content-bg">
                            <Form
                                colon={false}
                                layout="vertical"
                                form={form}
                                onValuesChange={onValuesChange}
                                initialValues={{ email: info?.user?.email || '' }}
                            >
                                <Form.Item label="お名前" name="customerName" rules={[{ required: true, message: '* は入力必須項目です' }]}>
                                    <Input maxLength={50}></Input>
                                </Form.Item>
                                <Form.Item label="メールアドレス" name="email" rules={[{ required: true, message: '* は入力必須項目です' }]}>
                                    <Input disabled></Input>
                                </Form.Item>
                                <Form.Item label="注文番号" name="orderId">
                                    <Input maxLength={50}></Input>
                                </Form.Item>
                                <Form.Item label="お問い合わせ内容" name="content" rules={[{ required: true, message: '* は入力必須項目です' }]}>
                                    <Input.TextArea maxLength={500} showCount></Input.TextArea>
                                </Form.Item>
                                <Form.Item name="fileUrlList" className="file-btn">
                                    <Upload
                                        action=""
                                        name="upload"
                                        multiple={true}
                                        accept="image/*,application/pdf"
                                        beforeUpload={handleBeforeUpload}
                                        onChange={handleUpload}
                                        customRequest={({ file, onSuccess, onError }: any) => customRequest({ file, onSuccess, onError })}
                                        maxCount={10}
                                    >
                                        <span className="file-link">ファイルを添付する</span>
                                    </Upload>
                                </Form.Item>
                            </Form>
                        </div>
                    </div>
                    <div className="dedicated-bottom">
                        <div className="dedicated-bottom-content">
                            <Checkbox onChange={handleChangeAgreed} checked={agreed}>
                                EcoFlow
                                <a className="link" href={'https://account.ecoflow.com/agreement/ja-jp/PrivacyPolicy.html'} target="_blank" rel="noreferrer">
                                    プライバシーポリシー
                                </a>{' '}
                                を読み、上記の情報が事前に収集されることに同意します。
                            </Checkbox>
                        </div>
                        <Button type="primary" block className="send-btn" onClick={handleSubmit} disabled={!(sendStatus && agreed)}>
                            送信
                        </Button>
                        <div className="dedicated-bottom-tip">*が付いているフィールドは必須です。</div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default DedicatedCustomer;
